import allStores from '@/data/Content/StoreLocator/storeLocatorStateCitiesList.json';
import { ProductAvailabilityData } from '@/data/types/ProductAvailabilityData';
/**
 * Inferred types from the JSON data structure.
 */
interface StateInfo {
	geonodeId: string;
	state: string;
}

interface Schedule {
	dayName: string;
	open: string;
	close: string;
}

interface DiystoreSchedule {
	Diystore_hours: string;
	Diystore_hours_7bucket: string;
	schedule: Schedule[];
}

interface ProstoreSchedule {
	prostore_hours: string;
	prostore_hours_7bucket: string;
	schedule: Schedule[];
}

export interface HDMStore {
	city: string;
	stLocId: string;
	marketId: string;
	stLocName: string;
	shortName: string;
	storeName: string;
	scheduleDiystore: DiystoreSchedule;
	scheduleProstore: ProstoreSchedule;
	address1: string;
	address2: string;
	cityId: string;
	zipcode: string;
	phone: string;
	latitude: string;
	longitude: string;
	uniqueID: string;
}

interface ScheduleObject {
	stateId: string;
	stores: HDMStore[];
}

interface StatesAndCitiesData {
	formStatesObject: StateInfo[];
	formScheduleObject: ScheduleObject[];
}

export interface City {
	stLocId: string;
	city: string;
	cityId: string;
}

export interface StateWithCities {
	stateId: string;
	state: string;
	cities: City[];
}

export interface StoreInventory {
	stateId: string;
	store: HDMStore;
	physicalStoreUniqueId: string;
	inventory: ProductAvailabilityData;
}

/**
 * storesGroupedByStateMap:  is organized and regrouped by stateId and its all related stores retrieved from storeLocatorStateCitiesList.json
 * The city names are unique per state.
 * {stateId,
 *  state,
 *  cities,
 * }
 */
export const buildStatesWithUniqueCities = (): StateWithCities[] => {
	const statesAndCitiesData = allStores as StatesAndCitiesData;
	const storesGroupedByStateMap = statesAndCitiesData.formScheduleObject;

	// Map over the primary list of states to build the new structure.

	const result = [] as StateWithCities[];
	statesAndCitiesData.formStatesObject.forEach((stateData) => {
		const storesInState =
			storesGroupedByStateMap.find((stateGroup: any) => stateData.geonodeId === stateGroup.stateId)
				?.stores || [];
		const uniqueCities = new Map();
		// Use reduce to build the uniqueCities Map
		storesInState.forEach((store) => {
			const cityName = store.city.trim();
			// Using a Map ensures we only get one entry per city name, effectively
			// taking the first one encountered, which matches the original logic.
			if (cityName && !uniqueCities.has(cityName)) {
				uniqueCities.set(cityName, {
					stLocId: store.stLocId,
					city: cityName,
					cityId: store.cityId,
				});
			}
		});

		result.push({
			stateId: stateData.geonodeId,
			state: stateData.state,
			// Convert the Map values back to an array and sort alphabetically for a better UX.
			cities: Array.from(uniqueCities.values()).sort((a, b) => a.city.localeCompare(b.city)),
		});
	});
	return result;
};

export const findAllStoresByStateId = (stateId: string): HDMStore[] => {
	const storesInState = allStores.formScheduleObject.find(
		(state: any) => state.stateId === stateId
	);
	return storesInState?.stores as HDMStore[];
};

export const findStoreDataByStLocId = (stLocId: string): ScheduleObject | undefined => {
	if (!stLocId) {
		return;
	}
	const statesData = allStores.formScheduleObject;
	return statesData.filter((states) =>
		states.stores.some((cities) => cities.stLocId === stLocId)
	)[0] as ScheduleObject;
};

export const findMarketIdByStLocId = (stLocId?: string | null): string => {
	if (!stLocId) {
		return '';
	}
	const store = (allStores.formScheduleObject as ScheduleObject[])
		.flatMap((state) => state.stores)
		.find((candidate) => candidate.stLocId === stLocId);
	return store?.marketId ?? '';
};

export const findStoreIdByZipCode = (zipCode?: string): string | undefined => {
	const normalized = zipCode?.trim();
	if (!normalized) {
		return;
	}
	const statesData = allStores.formScheduleObject as ScheduleObject[];
	const match = statesData
		.flatMap((state) => state.stores)
		.find((store) => store.zipcode === normalized);
	return match?.stLocId;
};
